import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Rows shown before the first row can be measured
const INITIAL_COUNT = 10;

/**
 * How many rows (elements marked with `data-result-row`) fit in a container.
 * Re-measures on every render and when the container resizes. Uses the tallest
 * row seen so far, which only grows, so the count settles instead of flickering
 * between pages with rows of different heights.
 * @returns {[React.RefObject, number]} Ref to attach to the container, and the row count
 */
export const useFitCount = () => {
    const containerRef = useRef(null);
    const rowHeightRef = useRef(0);
    const [count, setCount] = useState(INITIAL_COUNT);

    const measure = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        container.querySelectorAll('[data-result-row]').forEach((row) => {
            rowHeightRef.current = Math.max(rowHeightRef.current, row.getBoundingClientRect().height);
        });
        if (rowHeightRef.current > 0) {
            setCount(Math.max(1, Math.floor(container.clientHeight / rowHeightRef.current)));
        }
    }, []);

    // Rows change on data load and page change
    useLayoutEffect(measure);

    useEffect(() => {
        const observer = new ResizeObserver(measure);
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [measure]);

    return [containerRef, count];
};
